import { useEffect, useMemo, useState } from "react";
import { fetchMutations, mutationPhotoUrl } from "@/lib/api";
import { Search, X, Dna, FlaskConical, ArrowRight, Loader2, Camera } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ALL = "All";

// Photoreal AI portrait for a mutation. Lazy-loads on demand, shows a soft
// skeleton + loader while Nano Banana renders. Cached forever after the
// first request so subsequent visitors get it instantly.
function MutationPhoto({ mutation }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  const src = mutationPhotoUrl(mutation.id);
  return (
    <div
      data-testid={`mutation-photo-${mutation.id}`}
      className="relative w-full aspect-[4/3] border border-border bg-secondary overflow-hidden"
    >
      {!loaded && !errored && (
        <div className="absolute inset-0 grid place-items-center text-muted-foreground">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="animate-spin" size={20} />
            <span className="overline">Rendering portrait…</span>
            <span className="text-[10px] font-mono text-muted-foreground">
              First-ever render can take ~10s
            </span>
          </div>
        </div>
      )}
      {errored ? (
        <div className="absolute inset-0 grid place-items-center text-muted-foreground text-xs">
          <div className="flex flex-col items-center gap-1">
            <Camera size={18} />
            <span>Portrait unavailable</span>
          </div>
        </div>
      ) : (
        <img
          src={src}
          alt={`Photoreal portrait of a ${mutation.name} budgerigar`}
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={`w-full h-full object-cover transition-opacity duration-500 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
      <div className="absolute bottom-1.5 right-2 font-mono text-[9px] uppercase tracking-widest text-white/85 mix-blend-difference">
        AI · Nano Banana
      </div>
    </div>
  );
}

function MutationCard({ m, onOpen, index }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(m)}
      data-testid={`mutation-card-${m.id}`}
      className="text-left border border-border bg-card p-5 hover:border-primary/40 transition-colors fade-up"
      style={{ animationDelay: `${index * 25}ms` }}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-14 h-14 border border-border shrink-0"
          style={{ background: m.swatch }}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <div className="overline">{m.category}</div>
          <div className="font-bold tracking-tight text-lg mt-0.5">{m.name}</div>
          <div className="text-[11px] font-mono text-muted-foreground mt-1">
            {m.inheritance}
          </div>
        </div>
      </div>
      <p className="mt-4 text-sm text-muted-foreground line-clamp-3 leading-relaxed">
        {m.summary}
      </p>
    </button>
  );
}

function MutationDetail({ mutation, onClose }) {
  const navigate = useNavigate();
  if (!mutation) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
      onClick={onClose}
      data-testid="mutation-detail-modal"
    >
      <div
        className="bg-card border border-border max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 lg:p-8 border-b border-border flex items-start gap-5">
          <div
            className="w-20 h-20 border border-border shrink-0"
            style={{ background: mutation.swatch }}
          />
          <div className="flex-1 min-w-0">
            <div className="overline">{mutation.category}</div>
            <h2 className="text-3xl font-black tracking-tight mt-1">
              {mutation.name}
            </h2>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <span className="genotype-chip">{mutation.inheritance}</span>
              <span className="genotype-chip">Discovered: {mutation.discovered}</span>
            </div>
          </div>
          <button
            data-testid="mutation-detail-close"
            onClick={onClose}
            className="border border-border w-8 h-8 grid place-items-center hover:bg-secondary"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-6 lg:p-8 space-y-6">
          <MutationPhoto mutation={mutation} />

          <div>
            <div className="overline mb-2">Summary</div>
            <p className="leading-relaxed">{mutation.summary}</p>
          </div>

          <div>
            <div className="overline mb-2">Genetic details</div>
            <p className="leading-relaxed text-muted-foreground">
              {mutation.details}
            </p>
          </div>

          <div>
            <div className="overline mb-2">Genotype example</div>
            <code
              data-testid="mutation-genotype"
              className="block font-mono text-xs bg-secondary border border-border p-3 rounded-sm overflow-x-auto"
            >
              {mutation.genotype_example}
            </code>
          </div>

          <div className="border-t border-border pt-5 flex flex-wrap gap-3">
            <button
              data-testid="mutation-open-calculator"
              onClick={() => navigate("/calculator")}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors font-medium text-sm"
            >
              <FlaskConical size={14} /> Try this in the calculator
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Encyclopedia() {
  const [mutations, setMutations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(ALL);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchMutations()
      .then((d) => {
        if (mounted) setMutations(d);
      })
      .catch((e) => mounted && setError(e.message || "Failed to load"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const categories = useMemo(() => {
    const set = new Set(mutations.map((m) => m.category));
    return [ALL, ...Array.from(set)];
  }, [mutations]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return mutations.filter((m) => {
      if (category !== ALL && m.category !== category) return false;
      if (!q) return true;
      return (
        m.name.toLowerCase().includes(q) ||
        m.summary.toLowerCase().includes(q) ||
        m.inheritance.toLowerCase().includes(q) ||
        m.category.toLowerCase().includes(q)
      );
    });
  }, [mutations, query, category]);

  return (
    <div data-testid="encyclopedia-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <header className="mb-10">
        <div className="overline">Catalogue 03 · Mutation Index</div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">
          Worldwide Mutation Encyclopedia
        </h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Every documented budgerigar mutation — base colours, dark factors,
          ino series, pieds, opaline, spangle, anthracite, the legendary
          Rainbow, and more. Click any entry for full inheritance details.
        </p>
      </header>

      <div className="border border-border bg-card p-4 lg:p-5 mb-8">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              data-testid="mutation-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, trait, inheritance…"
              className="w-full bg-background border border-border pl-9 pr-3 py-2.5 rounded-sm text-sm focus:outline-none focus:border-primary"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`category-${c}`}
                onClick={() => setCategory(c)}
                className={[
                  "text-xs px-2.5 py-1.5 rounded-sm border transition-colors",
                  category === c
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-secondary",
                ].join(" ")}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="animate-spin" size={16} /> Loading the catalogue…
        </div>
      )}

      {error && (
        <div className="text-destructive text-sm">{error}</div>
      )}

      {!loading && !error && (
        <>
          <div className="mb-4 flex items-center justify-between">
            <div className="overline" data-testid="mutation-count">
              {filtered.length} of {mutations.length} entries
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <Dna size={11} className="inline -mt-0.5 mr-1" /> Melopsittacus
              undulatus
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((m, i) => (
              <MutationCard key={m.id} m={m} index={i} onOpen={setActive} />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-sm text-muted-foreground py-12 text-center border border-dashed border-border">
              No mutations match those filters.
            </div>
          )}
        </>
      )}

      <MutationDetail mutation={active} onClose={() => setActive(null)} />
    </div>
  );
}
